import React, { useState, useEffect } from "react";
import config from "../../config/config";
import { Doughnut, Pie } from "react-chartjs-2";
import { userSession } from "../../observers/userObserver";

import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";

Chart.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend
);

const COLORS = {
  active: "#006565", 
  inactive: "#00b3b3",
  billable: "#006565",
  nonBillable: "#bcffff",
};

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: "bottom",
      labels: {
        color: "#374151", // dark gray for readability
        font: {
          size: 14,
          weight: "600",
        },
      },
    },
    tooltip: {
      enabled: true,
    },
  },
};

const AdminAnalyticsComponent = () => {
  const [dashboardStats, setDashboardStats] = useState({
    completedRequests: 0,
    nonCompletedRequests: 0,
    activeRequests: 0,
    inactiveRequests: 0,
    activeUsers: 0,
    totalUsers: 0,
    billableRemaining: 0,
    billableUsed: 0,
    loading: true,
  });

  const {
    activeRequests,
    inactiveRequests,
    activeUsers,
    totalUsers,
    billableRemaining,
    billableUsed,
    completedRequests,
    nonCompletedRequests,
    loading,
  } = dashboardStats;

  const completedRequestChartData = {
    labels: ["Completed", "Active"],
    datasets: [
      {
        label: "Requests",
        data: [completedRequests, nonCompletedRequests],
        backgroundColor: [COLORS.active, COLORS.inactive],
      },
    ],
  };
// Dummy grey chart data
const dummyChartData = {
  labels: ["No Data A", "No Data B"],
  datasets: [
    {
      data: [70, 30], // 70% vs 30%
      backgroundColor: ["#d3d3d3", "#b0b0b0"], // light & dark grey
      borderWidth: 0,
    },
  ],
};

// Function to check if dataset has values


// Function to check if dataset has values
const hasData = (data) =>
  data.datasets[0].data.some((value) => value > 0);
  const activeRequestChartData = {
    labels: ["Active", "Inactive"],
    datasets: [
      {
        label: "User Status",
        data: [activeRequests, inactiveRequests],
        borderColor: ["#07b1b0", "#f44336"],
        backgroundColor: ["#07b1b0", "#ffcccb"],
        fill: true,
      },
    ],
  };

  const activeUserChartData = (activeUsers, totalUsers) => ({
    labels: ["Active", "Inactive/Pending"],
    datasets: [
      {
        data: [activeUsers, totalUsers - activeUsers],
        backgroundColor: [COLORS.active, COLORS.inactive],
      },
    ],
  });

  const billableEventChartData = {
    labels: ["Billable Used", "Billable Remaining"],
    datasets: [
      {
        data: [billableUsed, billableRemaining],
        backgroundColor: [COLORS.billable, COLORS.nonBillable],
      },
    ],
  };

  useEffect(() => {
    const billingStats = async () => {
      try {
        const response = await fetch(
          `${config["backend-url"]}/combined_usage_limits_by_user`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userSession.getToken()}`,
            },
          }
        );

        if (!response.ok) throw new Error("Failed to fetch billing stats");

        const data = await response.json();

        const used = data.Usage?.["Create Request"] || 0;
        const allowed = data.Limits?.["Create Request"] || 0;

        setDashboardStats((prev) => ({
          ...prev,
          billableUsed: used,
          billableRemaining: allowed - used,
        }));
      } catch (error) {
        console.error("❌ Error fetching billing stats:", error);
      }
    };

    billingStats();
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = userSession.getToken();
        const response = await fetch(`${config["backend-url"]}/admin_dashboard_data`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await response.json();

        if (response.ok) {
          setDashboardStats((prev) => ({
            ...prev,
            nonCompletedRequests: data.charts.inProgressRequests || 0,
            activeRequests: data.charts.inProgressRequests || 0,
            completedRequests: data.charts.closedRequests || 0,
            activeUsers: data.charts.activeUsers || 0,
            totalUsers: data.charts.totalUsers || 100,
            billableRemaining: data.charts.billableRemaining || 0,
            billableUsed: data.charts.billableUsed || 0,
            loading: false,
          }));
        } else {
          console.error("❌ Failed to load:", data.error);
          setDashboardStats((prev) => ({ ...prev, loading: false }));
        }
      } catch (error) {
        console.error("❌ Network or server error:", error);
        setDashboardStats((prev) => ({ ...prev, loading: false }));
      }
    };

    fetchDashboardData();
  }, []);

  return (
    <>
      {/* <style>{`
        * {
          box-sizing: border-box;
        }
        body {
          margin: 0;
          font-family: 'Poppins', sans-serif;
          background-color: #ffffff;
          color: #374151;
        }
        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 40px 20px 60px;
        }
        .dashboard-header {
          margin-bottom: 40px;
          text-align: center;
        }
        .welcomeText {
          font-size: 48px;
          font-weight: 700;
          margin: 0;
          color: #111827;
        }
        .dashboard-sections {
          display: flex;
          flex-wrap: wrap;
          gap: 24px;
          justify-content: center;
        }
        .dashboard-section {
          background: #fff;
          border-radius: 0.75rem;
          box-shadow: 0 1px 6px rgb(0 0 0 / 0.1);
          padding: 24px;
          flex: 1 1 320px;
          max-width: 380px;
          display: flex;
          flex-direction: column;
          align-items: center;
          min-height: 320px;
        }
        .kpi-column {
          max-width: 280px;
        }
        .kpi-wrapper {
          width: 100%;
        }
        .kpi-box {
          margin-bottom: 20px;
          padding: 16px;
          background: #f9fafb;
          border-radius: 0.75rem;
          box-shadow: inset 0 0 6px rgb(0 0 0 / 0.03);
          text-align: center;
        }
        .kpi-box h4 {
          font-weight: 600;
          font-size: 18px;
          margin-bottom: 8px;
          color: #4b5563;
        }
        .kpi-value {
          font-size: 32px;
          font-weight: 700;
          margin: 0;
          color: #111827;
        }
        .chart-container {
          width: 100%;
          height: 280px;
          position: relative;
        }
        h4 {
          margin-bottom: 16px;
          font-weight: 700;
          font-size: 24px;
          color: #111827;
        }
      `}</style> */}

      <div className="container">
        <div className="dashboard-header">
          <h1 className="welcomeText">Administration Interface</h1>
        </div>

        <div className="dashboard-sections" style={{ marginBottom: "20px" }}>
          {/* KPI Column */}
          <div className="dashboard-section kpi-column">
            <div className="kpi-wrapper">
              {[
                {
                  label: "Completed Request (based on MLNO confirmation flag)",
                  value: completedRequests,
                },
                { label: "Active Requests", value: activeRequests },
                { label: "Active Users", value: activeUsers },
              ].map((kpi, i) => (
                <div className="kpi-box" key={i}>
                  <h4>{kpi.label}</h4>
                  <p className="kpi-value">{kpi.value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Active vs Completed Request Chart */}
          {/* Completed vs Active Request Chart */}
<div className="dashboard-section bar-column">
  <h4>Completed vs Active Requests</h4>
  <div className="chart-container" style={{ textAlign: "center" }}>
    <div
      style={{
        width:
          (Number(completedRequests) || 0) > 0 ||
          (Number(nonCompletedRequests) || 0) > 0
            ? "350px" // normal chart size
            : "200px", // smaller dummy chart
        height:
          (Number(completedRequests) || 0) > 0 ||
          (Number(nonCompletedRequests) || 0) > 0
            ? "350px"
            : "200px",
        margin: "0 auto",
      }}
    >
      <Doughnut
        data={
          (Number(completedRequests) || 0) > 0 ||
          (Number(nonCompletedRequests) || 0) > 0
            ? completedRequestChartData // real data
            : dummyChartData            // fallback
        }
        options={{
          ...chartOptions,
          maintainAspectRatio: false, // so custom size applies
          cutout:
            (Number(completedRequests) || 0) > 0 ||
            (Number(nonCompletedRequests) || 0) > 0
              ? "60%"
              : "75%", // thinner + smaller ring for dummy
          plugins: {
            ...chartOptions.plugins,
            legend: {
              ...chartOptions.plugins.legend,
              display:
                (Number(completedRequests) || 0) > 0 ||
                (Number(nonCompletedRequests) || 0) > 0,
            },
            tooltip: {
              enabled:
                (Number(completedRequests) || 0) > 0 ||
                (Number(nonCompletedRequests) || 0) > 0,
            },
          },
        }}
        aria-label="Completed vs Active Requests Chart"
        role="img"
      />
    </div>

    {/* Label shown only for dummy */}
    {!(Number(completedRequests) || 0) &&
      !(Number(nonCompletedRequests) || 0) && (
        <p
          style={{
            marginTop: "12px",
            color: "#666",
            fontStyle: "italic",
            fontSize: "14px",
          }}
        >
          No requests have been created
        </p>
      )}
  </div>
</div>



        
          {/* Active User Pie Chart */}
          <div className="dashboard-section pie-column">
            <h4>Active Users V/S Inactive Users</h4>
            <p className="kpi-value">{activeUsers}</p>
            <div className="chart-container">
              <Pie
                data={activeUserChartData(activeUsers, totalUsers)}
                options={chartOptions}
                aria-label="Active vs Inactive Users Chart"
                role="img"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminAnalyticsComponent;

